import { useEffect, useState } from 'react';
import Hint from '../Hint';
import BodyTypePicker from '../BodyTypePicker';
import type { RateHint } from 'shared';
import {
  CITIES,
  TRANSPORT_MODES,
  TRANSPORT_MODE_HINT,
  TRANSPORT_MODE_LABEL,
  usesBodyTypes,
  usesRoadDistance,
  deliveryEta,
  PAYMENT_TYPES,
  PAYMENT_TYPE_LABEL,
  LOADING_TYPES,
  LOADING_TYPE_LABEL,
  type LoadingType,
  estimateRoadKm,
  haversineKm,
  formatMoney,
  currencyOfCountry,
  CURRENCY_SIGN,
  BIDDING_MODES,
  BIDDING_MODE_LABEL,
  BIDDING_MODE_HINT,
  type BiddingMode,
  isCurrency,
  type Currency,
  type BodyType,
  type PaymentType,
  type TransportMode,
} from 'shared';
import { api } from '../api';
import PlacePicker, { type PlaceValue } from '../PlacePicker';
import { alert, haptic, notify } from '../telegram';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';
import Icon from '../Icon';
import { useMoney } from '../money';

interface Props {
  onPublished: () => void;
  /**
   * Правка существующей заявки вместо публикации новой.
   *
   * Та же форма, а не отдельный экран: две формы для одних и тех же
   * полей расходятся на первой же правке — сначала в валидации, потом
   * в наборе полей, и половина условий начинает задаваться только при
   * создании.
   */
  editId?: string;
  /**
   * Черновик быстрой заявки из бота: та же форма, но поля подставлены
   * из разобранного текста, а чего парсер не понял — человек заполняет.
   */
  draftId?: string;
}

export default function Publish({ onPublished, editId, draftId }: Props) {
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState<TransportMode>('AUTO');
  const [modes, setModes] = useState<TransportMode[]>(['AUTO']);
  /** Быстрые кнопки кузовов — свои у каждого, из профиля. */
  const [favorites, setFavorites] = useState<BodyType[]>([]);
  const [from, setFrom] = useState<PlaceValue | null>({
    address: 'Москва',
    region: CITIES['Москва']!.region,
    country: CITIES['Москва']!.country,
    lat: CITIES['Москва']!.lat,
    lng: CITIES['Москва']!.lng,
  });
  const [to, setTo] = useState<PlaceValue | null>({
    address: 'Санкт-Петербург',
    region: CITIES['Санкт-Петербург']!.region,
    country: CITIES['Санкт-Петербург']!.country,
    lat: CITIES['Санкт-Петербург']!.lat,
    lng: CITIES['Санкт-Петербург']!.lng,
  });
  const [weightT, setWeightT] = useState('20');
  const [volume, setVolume] = useState('');
  const [bodyTypes, setBodyTypes] = useState<BodyType[]>(['TENT']);
  const [price, setPrice] = useState('');
  /*
   * Валюта заявки.
   *
   * Подставляется по стране загрузки: перевозка внутри Казахстана
   * назначается в тенге, и заставлять логиста каждый раз менять рубль на
   * тенге — это налог на то, что площадка русскоязычная.
   */
  const [currency, setCurrency] = useState<Currency>('RUB');
  const [currencyTouched, setCurrencyTouched] = useState(false);
  /*
   * Режим торга. По умолчанию обычный отклик — аукцион нужен не всегда, а
   * ставить его умолчанием значит навязывать торг тому, кто просто ищет
   * машину на завтра.
   */
  const [biddingMode, setBiddingMode] = useState<BiddingMode>('NONE');
  const [bidEnds, setBidEnds] = useState('');
  const [bidStep, setBidStep] = useState('');
  const { options, rate: rateOf } = useMoney();
  const dealCurrencies = options.filter((o) => o.deal);
  const [paymentType, setPaymentType] = useState<PaymentType>('BANK');
  const [delayDays, setDelayDays] = useState('0');
  const [vat, setVat] = useState(false);
  const [loadDate, setLoadDate] = useState(new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState('');
  /** Точный адрес — необязателен: направление обсуждают раньше склада. */
  const [fromDetail, setFromDetail] = useState('');
  const [toDetail, setToDetail] = useState('');

  /*
   * Догруз. Отдельный флаг, а не вывод из веса: пять тонн — это и полная
   * загрузка «газели», и догруз в фуру. Решает отправитель, потому что
   * он же соглашается на то, что машина поедет не напрямую.
   */
  const [partial, setPartial] = useState(false);
  const [lengthM, setLengthM] = useState('');
  const [detourKm, setDetourKm] = useState('200');
  const [hint, setHint] = useState<RateHint | null>(null);
  const [craneCapacityT, setCraneCapacityT] = useState('');
  const [craneReachM, setCraneReachM] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useFormErrors();
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [audienceGroupId, setAudienceGroupId] = useState('');

  /* Дополнительные поля — общие для формы, из чат-шаблона приходят чаще всего. */
  const [vehicles, setVehicles] = useState('1');
  const [contactPhone, setContactPhone] = useState('');
  const [advanceNote, setAdvanceNote] = useState('');
  const [customsNote, setCustomsNote] = useState('');
  const [roadTrain, setRoadTrain] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState<LoadingType[]>([]);

  // Подтягиваем заявку в форму. Пока не загрузилась — форма показывает
  // значения по умолчанию, и сохранить её нельзя: иначе можно случайно
  // переписать заявку пустышкой.
  const [loaded, setLoaded] = useState(!editId && !draftId);

  useEffect(() => {
    if (!editId) return;
    api
      .cargo(editId)
      .then((c) => {
        setTitle(c.title);
        setMode((c.transportMode ?? 'AUTO') as TransportMode);
        setFrom({
          address: c.fromAddress,
          region: c.fromRegion,
          country: c.fromCountry ?? null,
          lat: c.fromLat,
          lng: c.fromLng,
        });
        setTo({
          address: c.toAddress,
          region: c.toRegion,
          country: c.toCountry ?? null,
          lat: c.toLat,
          lng: c.toLng,
        });
        setWeightT(String(c.weightKg / 1000));
        setVolume(c.volumeM3 ? String(c.volumeM3) : '');
        setBodyTypes(c.bodyTypes as BodyType[]);
        setPrice(String(c.price));
        setBiddingMode(c.biddingMode);
        setBidEnds(c.bidEndsAt ? c.bidEndsAt.slice(0, 16) : '');
        setBidStep(c.bidStep ? String(c.bidStep) : '');
        if (isCurrency(c.currency)) {
          setCurrency(c.currency);
          // Правка опубликованной заявки: валюта уже выбрана, и подменять
          // её страной адреса нельзя — это изменило бы цену сделки.
          setCurrencyTouched(true);
        }
        setPaymentType(c.paymentType as PaymentType);
        setDelayDays(String(c.delayDays ?? 0));
        setVat(c.vatIncluded);
        setLoadDate(new Date(c.loadDateFrom).toISOString().slice(0, 10));
        setComment(c.comment ?? '');
        setFromDetail(c.fromAddressDetail ?? '');
        setToDetail(c.toAddressDetail ?? '');
        setPartial(c.partial);
        setLengthM(c.lengthM ? String(c.lengthM) : '');
        setDetourKm(c.detourKm ? String(c.detourKm) : '200');
        setCraneCapacityT(c.craneCapacityKg ? String(c.craneCapacityKg / 1000) : '');
        setCraneReachM(c.craneReachM ? String(c.craneReachM) : '');
        setVehicles(String(c.vehiclesCount ?? 1));
        setContactPhone(c.contacts?.contactPhone ?? '');
        setAdvanceNote(c.advanceNote ?? '');
        setCustomsNote(c.customsNote ?? '');
        setRoadTrain(c.roadTrain ?? false);
        setLoadingTypes((c.loadingTypes ?? []) as LoadingType[]);
        setLoaded(true);
      })
      .catch((e: Error) => setError(e.message));
  }, [editId]);

  /*
   * Черновик быстрой заявки: поля из разобранного текста. Всё
   * необязательно — чего парсер не понял, человек вводит сам, поэтому
   * ставим только то, что реально пришло.
   */
  useEffect(() => {
    if (!draftId) return;
    api
      .cargoDraft(draftId)
      .then(({ parsed: p }) => {
        if (p.title) setTitle(p.title);
        if (p.from) {
          setFrom({
            address: p.from.address,
            region: p.from.region,
            country: p.from.country ?? null,
            lat: p.from.lat,
            lng: p.from.lng,
          });
        }
        if (p.to) {
          setTo({
            address: p.to.address,
            region: p.to.region,
            country: p.to.country ?? null,
            lat: p.to.lat,
            lng: p.to.lng,
          });
        }
        if (p.weightKg) setWeightT(String(p.weightKg / 1000));
        if (p.volumeM3) setVolume(String(p.volumeM3));
        if (p.bodyTypes.length) setBodyTypes(p.bodyTypes);
        if (p.price) setPrice(String(p.price));
        if (p.currency && isCurrency(p.currency)) {
          setCurrency(p.currency);
          setCurrencyTouched(true);
        }
        if (p.paymentType) setPaymentType(p.paymentType);
        if (p.loadDates[0]) setLoadDate(p.loadDates[0]);
        if (p.vehiclesCount) setVehicles(String(p.vehiclesCount));
        if (p.contactPhone) setContactPhone(p.contactPhone);
        if (p.advanceNote) setAdvanceNote(p.advanceNote);
        if (p.customsNote) setCustomsNote(p.customsNote);
        if (p.roadTrain) setRoadTrain(true);
        if (p.loadingTypes.length) setLoadingTypes(p.loadingTypes);
        // Всё, что парсер не разложил по полям, — в комментарий: лишние
        // даты погрузки, «каждый день по 20 авто», случайные строки.
        const extra = [
          p.comment,
          p.loadDateText,
          p.loadDates.length > 1 ? `Даты погрузки: ${p.loadDates.join(', ')}` : null,
        ]
          .filter(Boolean)
          .join('\n');
        if (extra) setComment(extra);
        setLoaded(true);
      })
      .catch((e: Error) => {
        setError(`Черновик не открылся: ${e.message}`);
        setLoaded(true);
      });
  }, [draftId]);

  useEffect(() => {
    api.myGroups().then((r) => setGroups(r.items)).catch(() => setGroups([]));
    // Какие виды транспорта открыл администратор. Молча падаем на
    // автомобиль: он включён всегда, и форма должна работать даже если
    // настройки не отдались.
    api
      .settings()
      .then((s) => setModes(s.transportModes?.length ? s.transportModes : ['AUTO']))
      .catch(() => setModes(['AUTO']));
    api
      .me()
      .then((m) => setFavorites(m.favoriteBodyTypes ?? []))
      .catch(() => setFavorites([]));
  }, []);

  // Расстояние по дорогам — только для автомобиля: между портами и
  // аэропортами дорожной сети нет, там честнее прямая.
  const distance =
    from && to
      ? usesRoadDistance(mode)
        ? estimateRoadKm(from.lat, from.lng, to.lat, to.lng)
        : Math.round(haversineKm(from.lat, from.lng, to.lat, to.lng))
      : null;
  /*
   * Ставка за километр — в рублях, и подпись «₽/км» не врёт.
   *
   * Медиана рынка и порог «ниже рынка» считаются в рублях: лента
   * смешивает валюты, сравнивать тенге с рублями нельзя. Поэтому саму
   * ставку приводим к рублям, а не переименовываем единицу.
   */
  const dealRate = rateOf(currency) ?? 1;
  const priceRubValue = Number(price) * dealRate;
  const perKm =
    distance && priceRubValue > 0 ? Math.round(priceRubValue / distance) : null;

  /** Подсказка рынка, переведённая в валюту заявки. */
  const suggested =
    hint?.suggestedRub != null && dealRate > 0
      ? Math.round(hint.suggestedRub / dealRate)
      : null;

  // Подсказка ставки по закрытым сделкам на этом направлении.
  // Логист иначе вводит цену вслепую: занизил — откликов нет, завысил — переплатил.
  useEffect(() => {
    if (!from || !to) return;
    const t = setTimeout(() => {
      api
        .rateHint({
          fromRegion: from.region,
          toRegion: to.region,
          bodyType: bodyTypes[0],
          distanceKm: distance ?? undefined,
        })
        .then(setHint)
        .catch(() => setHint(null));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, bodyTypes[0], distance]);

  async function submit() {
    /*
     * Все промахи собираем разом, а не выходим на первом.
     *
     * Иначе форма исправляется по одному полю за подход: нажал, доехал до
     * ошибки, поправил, нажал снова — и снова ошибка. Порядок в объекте
     * задаёт, к какому полю прокрутить: он совпадает с порядком на экране.
     */
    const bad: Record<string, string> = {};

    if (!from || !to) bad.from = 'Укажите места загрузки и выгрузки';
    else if (from.address === to.address) bad.to = 'Загрузка и выгрузка совпадают';

    const weightKg = Math.round(Number(weightT) * 1000);
    const priceValue = Math.round(Number(price));

    if (!weightKg) bad.weight = 'Укажите вес груза';
    // Отдельная машина берётся от тонны, меньше — только сборным грузом.
    else if (weightKg < 1000 && !partial) {
      bad.weight = 'Минимум для отдельной машины — 1 т. Меньше — отметьте «догруз»';
    }

    if (!loadDate) bad.loadDate = 'Укажите дату загрузки';
    // Кузов — свойство автомобиля. У вагона, судна и самолёта на его
    // месте другие характеристики, и требовать «тент» бессмысленно.
    if (usesBodyTypes(mode) && bodyTypes.length === 0) {
      bad.bodyTypes = 'Выберите хотя бы один тип кузова';
    }
    if (!priceValue) bad.price = 'Укажите ставку';
    if (biddingMode === 'AUCTION' && !bidEnds) {
      bad.bidEnds = 'Укажите, когда закрываются торги';
    }

    if (Object.keys(bad).length > 0) {
      notify('error');
      return form.fail(bad);
    }

    form.reset();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        title: title || `${from!.address} → ${to!.address}`,
        weightKg,
        volumeM3: volume ? Number(volume) : undefined,
        transportMode: mode,
        fromAddress: from!.address,
        fromRegion: from!.region,
        fromLat: from!.lat,
        fromLng: from!.lng,
        fromCountry: from!.country,
        toAddress: to!.address,
        toRegion: to!.region,
        toLat: to!.lat,
        toLng: to!.lng,
        toCountry: to!.country,
        loadDateFrom: new Date(loadDate),
        bodyTypes: usesBodyTypes(mode) ? bodyTypes : [],
        price: priceValue,
        currency,
        biddingMode,
        bidEndsAt: biddingMode === 'AUCTION' && bidEnds ? new Date(bidEnds) : undefined,
        bidStep: biddingMode === 'AUCTION' ? Math.round(Number(bidStep) || 0) : 0,
        vatIncluded: vat,
        paymentType,
        delayDays: Number(delayDays) || 0,
        craneCapacityKg: craneCapacityT
          ? Math.round(Number(craneCapacityT) * 1000)
          : null,
        craneReachM: craneReachM ? Number(craneReachM) : null,
        partial,
        lengthM: partial && lengthM ? Number(lengthM) : null,
        detourKm: partial && detourKm ? Number(detourKm) : null,
        vehiclesCount: Math.min(50, Math.max(1, Math.round(Number(vehicles) || 1))),
        contactPhone: contactPhone.trim() || null,
        advanceNote: advanceNote.trim() || null,
        customsNote: customsNote.trim() || null,
        roadTrain,
        loadingTypes,
        comment: comment || undefined,
        // Пустая строка — это «стереть адрес», а не «не трогать»:
        // в правке отличие значимое, поэтому шлём null, а не undefined.
        fromAddressDetail: fromDetail.trim() || null,
        toAddressDetail: toDetail.trim() || null,
      };

      if (editId) {
        // Вид транспорта и закрытую публикацию не правим: они меняют
        // смысл заявки настолько, что честнее снять её и опубликовать
        // заново, чем переписать под уже собранными откликами.
        await api.updateCargo(editId, payload);
        notify('success');
        alert('Заявка изменена. Откликнувшимся ушло уведомление, если поменялись условия.');
      } else {
        const res = await api.createCargo({
          ...payload,
          ttlHours: 72,
          audienceGroupId: audienceGroupId || null,
        });
        notify('success');
        // Черновик из бота больше не нужен — публикация состоялась.
        if (draftId) await api.dropCargoDraft(draftId).catch(() => {});
        alert(
          res.moderation
            ? 'Заявка отправлена на модерацию. Она появится в ленте после проверки диспетчером.'
            : 'Заявка опубликована. Водители с подходящей подпиской уже получили её.',
        );
      }
      onPublished();
    } catch (e) {
      notify('error');
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2>{editId ? 'Изменить заявку' : 'Новая заявка'}</h2>
      {error && <div className="error">{error}</div>}

      <div className="card">
        {/* Вид транспорта показываем, только если администратор открыл
            больше одного: с единственным автомобилем это лишний вопрос
            без выбора. */}
        {!editId && modes.length > 1 && (
          <div className="field">
            <label>Чем везём</label>
            <div className="seg">
              {TRANSPORT_MODES.filter((m) => modes.includes(m)).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={mode === m ? 'seg__btn seg__btn--on' : 'seg__btn'}
                  onClick={() => {
                    haptic();
                    setMode(m);
                  }}
                >
                  {TRANSPORT_MODE_LABEL[m]}
                </button>
              ))}
            </div>
            <div className="meta" style={{ marginTop: 4 }}>{TRANSPORT_MODE_HINT[mode]}</div>
          </div>
        )}

        <div className="field-row" data-tour="publish-route">
          <div className={`field${form.has('from') ? ' invalid-group' : ''}`} ref={form.group('from').ref}>
            <label>Откуда</label>
            <PlacePicker
              label="Место загрузки"
              value={from}
              onChange={(c) => {
                form.clear('from');
                setFrom(c);
                /*
                 * Валюта подставляется от страны загрузки, но только пока
                 * логист её не тронул сам: иначе выбранный вручную доллар
                 * слетал бы обратно на тенге при правке адреса.
                 */
                if (!currencyTouched) {
                  // Подставляем валюту страны, только если она включена:
                  // иначе форма предложит то, чего сервер не примет.
                  const guess = currencyOfCountry(c.country);
                  if (dealCurrencies.some((o) => o.code === guess)) setCurrency(guess);
                }
              }}
            />
          </div>
          <div className={`field${form.has('to') ? ' invalid-group' : ''}`} ref={form.group('to').ref}>
            <label>Куда</label>
            <PlacePicker
              label="Место выгрузки"
              value={to}
              onChange={(c) => {
                form.clear('to');
                setTo(c);
              }}
            />
          </div>
        </div>

        {/* Точный адрес — под маршрутом и необязательный. Спрашивать его
            наравне с городом нельзя: на бирже сначала договариваются о
            направлении, а склад называют уже исполнителю. Виден он тоже
            только сторонам сделки. */}
        <div className="field-row">
          <div className="field">
            <label>Точный адрес забора</label>
            <input
              placeholder="Улица, дом, ворота — необязательно"
              value={fromDetail}
              onChange={(e) => setFromDetail(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Точный адрес доставки</label>
            <input
              placeholder="Необязательно"
              value={toDetail}
              onChange={(e) => setToDetail(e.target.value)}
            />
          </div>
        </div>
        <div className="meta" style={{ marginTop: -4, marginBottom: 8 }}>
          Виден только вам и перевозчику, чей отклик вы примете.
        </div>

        {distance && (
          <div className="meta">
            Расчётное расстояние: ~{distance} км
            {deliveryEta(distance) && ` · в пути ${deliveryEta(distance)!.label}`}
            <Hint k="eta" />
          </div>
        )}
      </div>

      <div className="card">
        <div className="field">
          <label>Что везём</label>
          <input
            placeholder="Например: паллеты с бытовой химией"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Догруз — здесь же, под названием груза.
            Это свойство самого груза, а не отдельное условие перевозки:
            «занимает часть кузова» отвечает на тот же вопрос «что везём»,
            что и вес с объёмом. Отдельной карточкой внизу он читался как
            не связанный ни с чем переключатель, и его пропускали. */}
        <label className="check">
          <input
            type="checkbox"
            checked={partial}
            onChange={(e) => setPartial(e.target.checked)}
          />
          Можно догрузом
        </label>
        <div className="meta">
          Груз занимает часть кузова и поедет попутно с чужим. Дешевле полной
          машины, но срок зависит от маршрута перевозчика.
        </div>

        {partial && (
          <div className="field-row" style={{ marginTop: 'var(--s3)' }}>
            <div className="field">
              <label>Сколько займёт, м</label>
              <input
                inputMode="decimal"
                placeholder="например, 2.4"
                value={lengthM}
                onChange={(e) => setLengthM(e.target.value)}
              />
              <span className="meta">
                Метры кузова. Перевозчику важнее веса: он считает, влезет ли
                к тому, что уже стоит в машине.
              </span>
            </div>
            <div className="field">
              <label>Допустимый крюк, км</label>
              <input
                inputMode="numeric"
                placeholder="200"
                value={detourKm}
                onChange={(e) => setDetourKm(e.target.value)}
              />
              <span className="meta">
                Насколько перевозчик может отклониться от своего маршрута.
              </span>
            </div>
          </div>
        )}

        <div className="divider" />

        <div className="field-row">
          <div className="field">
            <label>
              Вес, т <Hint k="weight" />
            </label>
            <input
              {...form.field('weight')}
              type="number"
              inputMode="decimal"
              value={weightT}
              onChange={(e) => setWeightT(e.target.value)}
            />
            <FieldError form={form} name="weight" />
          </div>
          <div className="field">
            <label>Объём, м³</label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="не указан"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
            />
          </div>
        </div>

        {/* Дата не тянется на всю ширину: поле шириной с само значение
            читается как значение, а поле на весь экран — как незаполненное. */}
        <div className="field field--date">
          <label>Дата загрузки</label>
          <input
            {...form.field('loadDate')}
            type="date"
            value={loadDate}
            onChange={(e) => setLoadDate(e.target.value)}
          />
          <FieldError form={form} name="loadDate" />
        </div>

        {usesBodyTypes(mode) && (
          <div className="field">
            <label>
              Подходящий кузов <Hint k="bodyType" />
            </label>
            <div {...form.group('bodyTypes')}>
              <BodyTypePicker
                value={bodyTypes}
                onChange={(v) => {
                  form.clear('bodyTypes');
                  setBodyTypes(v);
                }}
                favorites={favorites}
                label="Подходящий кузов"
              />
            </div>
            <FieldError form={form} name="bodyTypes" />
          </div>
        )}
      </div>

      {usesBodyTypes(mode) && bodyTypes.includes('MANIPULATOR') && (
        <div className="card">
          <h3>
            Требования к манипулятору <Hint k="manipulator" />
          </h3>
          <div className="field-row">
            <div className="field">
              <label>Поднять, т</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="самое тяжёлое место"
                value={craneCapacityT}
                onChange={(e) => setCraneCapacityT(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Вылет стрелы, м</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="до точки разгрузки"
                value={craneReachM}
                onChange={(e) => setCraneReachM(e.target.value)}
              />
            </div>
          </div>
          <div className="meta">
            Перевозчики с манипулятором увидят заявку в отдельном фильтре
            и сразу поймут, подходит ли их техника.
          </div>
        </div>
      )}

      <div className="card">
        {/* Ставка, способ оплаты и отсрочка — одна строка: это три части
            одного ответа на вопрос «за сколько и на каких условиях».
            На узком экране `.field-row` переносит их сама. */}
        <div className="field-row field-row--wrap">
          <div className="field field--price">
            <label>Ставка</label>
            <div className="price-input">
              <input
                {...form.field('price')}
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              {/* Валюта рядом с суммой, а не отдельным полем ниже: это одно
                  число, и разносить его на два поля значит заставлять
                  проверять, к чему относится выбранная валюта. */}
              {/* Валюты — из справочника площадки, а не из константы в
                  коде: какие включены, решает администратор. Пока список
                  не загрузился, остаётся рубль — назначить сделку в нём
                  можно всегда. */}
              <select
                aria-label="Валюта"
                value={currency}
                onChange={(e) => {
                  setCurrencyTouched(true);
                  setCurrency(e.target.value as Currency);
                }}
              >
                {dealCurrencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.sign} {c.code}
                  </option>
                ))}
              </select>
            </div>
            <FieldError form={form} name="price" />
          </div>
          <div className="field field--pay">
            <label>Оплата</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as PaymentType)}
            >
              {PAYMENT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {PAYMENT_TYPE_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--delay">
            {/* Коротко: подпись со знаком вопроса должна умещаться в одну
                строку самого узкого поля формы, иначе поле встаёт ниже
                соседей. «дн.» вместо «дней» — та же мысль на два знака. */}
            <label>
              Отсрочка, дн. <Hint k="delayDays" />
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={delayDays}
              onChange={(e) => setDelayDays(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          {hint?.available && (
            <div className="card card--inset" style={{ marginTop: 8 }}>
              <div className="meta">
                По закрытым сделкам{hint.basis === 'region' ? ' из этого региона' : ' на этом направлении'}
                {` · ${hint.samples} сделок`}
              </div>
              <div className="price" style={{ fontSize: 17 }}>
                {hint.medianPerKm} ₽/км
                <span className="per-km">
                  {' '}· разброс {hint.lowPerKm}–{hint.highPerKm}
                </span>
              </div>
              {/*
                * Подсказка приходит в рублях — медиана рынка считается в
                * них. Подставлять её как есть нельзя: при валюте заявки
                * в тенге кнопка «Подставить 92 000 ₽» вписывала 92 000 ₸,
                * то есть впятеро дешевле рынка, и логист об этом не
                * узнавал до полного отсутствия откликов.
                */}
              {suggested !== null ? (
                <button
                  className="secondary"
                  style={{ marginTop: 8 }}
                  onClick={() => setPrice(String(suggested))}
                >
                  Подставить {formatMoney(suggested, currency)}
                </button>
              ) : null}
            </div>
          )}

          {perKm ? (
            <div className="meta" style={{ marginTop: 8 }}>
              {formatMoney(Number(price), currency)} — это {perKm} ₽/км
              {perKm < 60 ? ' · ниже рынка, откликов может не быть' : ''}
            </div>
          ) : null}
        </div>

        {/* Режим торга стоит рядом со ставкой: он меняет её смысл. При
            аукционе названная цена — не предложение, а потолок. */}
        <div className="field">
          <label>Как ищем исполнителя</label>
          <div className="seg">
            {BIDDING_MODES.map((m) => (
              <button
                key={m}
                type="button"
                className={biddingMode === m ? 'seg__btn seg__btn--on' : 'seg__btn'}
                onClick={() => setBiddingMode(m)}
              >
                {BIDDING_MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <div className="meta" style={{ marginTop: 'var(--s1)' }}>{BIDDING_MODE_HINT[biddingMode]}</div>
        </div>

        {biddingMode === 'AUCTION' && (
          <div className="field-row field-row--wrap">
            <div className="field">
              <label>Торги закрываются</label>
              <input
                {...form.field('bidEnds')}
                type="datetime-local"
                value={bidEnds}
                onChange={(e) => setBidEnds(e.target.value)}
              />
              <FieldError form={form} name="bidEnds" />
            </div>
            <div className="field">
              <label>Шаг понижения, {CURRENCY_SIGN[currency]}</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={bidStep}
                onChange={(e) => setBidStep(e.target.value)}
              />
              {/* Без шага торг вырождается в подрезание на единицу:
                  выигрывает не цена, а частота нажатий. */}
              <div className="meta">Без шага перебивают на единицу</div>
            </div>
          </div>
        )}

        <button type="button" className={`chip ${vat ? 'on' : ''}`} onClick={() => setVat((v) => !v)}>
          Ставка с НДС
        </button>
      </div>

      <div className="card">
        <h3>Дополнительно</h3>

        <div className="field-row">
          <div className="field">
            <label>Сколько машин</label>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              value={vehicles}
              onChange={(e) => setVehicles(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Телефон по заявке</label>
            <input
              inputMode="tel"
              placeholder="если звонить не автору"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label>Аванс</label>
          <input
            placeholder="например, есть 1000 $"
            value={advanceNote}
            onChange={(e) => setAdvanceNote(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Таможня</label>
          <input
            placeholder="например, растаможка в Нукусе"
            value={customsNote}
            onChange={(e) => setCustomsNote(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Погрузка / выгрузка</label>
          <div className="chips">
            {LOADING_TYPES.map((lt) => (
              <button
                key={lt}
                type="button"
                className={loadingTypes.includes(lt) ? 'chip chip--on' : 'chip'}
                onClick={() =>
                  setLoadingTypes((prev) =>
                    prev.includes(lt) ? prev.filter((x) => x !== lt) : [...prev, lt],
                  )
                }
              >
                {LOADING_TYPE_LABEL[lt]}
              </button>
            ))}
          </div>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={roadTrain}
            onChange={(e) => setRoadTrain(e.target.checked)}
          />
          Сцепка / автопоезд («паровоз»)
        </label>
      </div>

      {!editId && groups.length > 0 && (
        <div className="card">
          <div className="field" style={{ marginBottom: 0 }}>
            <label>
              Кому показать <Hint k="audience" />
            </label>
            <select
              value={audienceGroupId}
              onChange={(e) => setAudienceGroupId(e.target.value)}
            >
              <option value="">Всем перевозчикам</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  Только группе «{g.name}»
                </option>
              ))}
            </select>
            {audienceGroupId && (
              <div className="meta" style={{ marginTop: 8 }}>
                Закрытая публикация: в общей ленте заявки не будет.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Комментарий для водителя</label>
          <textarea
            rows={3}
            placeholder="Требования к машине, особенности погрузки, контакты на месте"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>
      </div>

      <button disabled={busy || !loaded} onClick={() => void submit()}>
        {busy
          ? editId
            ? 'Сохраняю…'
            : 'Публикую…'
          : editId
            ? 'Сохранить изменения'
            : 'Опубликовать заявку'}
      </button>

      {/* Сводка под кнопкой: человек нажал и смотрит сюда. Поля наверху
          уже подсвечены, но с длинной формы их не видно. */}
      {Object.keys(form.errors).length > 0 && (
        <div className="form__err" role="alert">
          <Icon name="close" size={16} />
          <span>
            {Object.keys(form.errors).length === 1
              ? Object.values(form.errors)[0]
              : `Не заполнено полей: ${Object.keys(form.errors).length}. Они отмечены выше.`}
          </span>
        </div>
      )}
    </>
  );
}
